import React, { useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { SleepChart } from './SleepChart';
import type { SleepLog } from '../types';

const QualityRating: React.FC<{ quality: number; setQuality: (q: number) => void }> = ({ quality, setQuality }) => {
  return (
    <div className="flex items-center justify-center gap-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <button key={star} onClick={() => setQuality(star)} className="text-3xl transition-transform hover:scale-110">
          <span className={star <= quality ? 'text-yellow-400' : 'text-slate-600'}>★</span>
        </button>
      ))}
    </div>
  );
};

export const SleepTracker: React.FC = () => {
  const { user, addSleepLog } = useUser();
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [duration, setDuration] = useState(8);
  const [quality, setQuality] = useState(3);
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (duration > 0 && duration <= 24) {
      const newLog: SleepLog = { 
        id: Date.now().toString(),
        date, 
        duration, 
        quality,
        bedtime: '',
        wakeTime: '',
        notes: '',
        createdAt: new Date().toISOString()
      };
      addSleepLog(newLog);
      setMessage('Sleep log saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } else {
      setMessage('Please enter a valid duration between 0 and 24 hours.');
      setTimeout(() => setMessage(''), 3000);
    }
  };
  
  const averageDuration = user?.sleepLog && user.sleepLog.length > 0
    ? (user.sleepLog.reduce((acc, log) => acc + log.duration, 0) / user.sleepLog.length).toFixed(1)
    : 'N/A';
  
  const averageQuality = user?.sleepLog && user.sleepLog.length > 0
    ? (user.sleepLog.reduce((acc, log) => acc + log.quality, 0) / user.sleepLog.length).toFixed(1)
    : 'N/A';

  return (
    <div className="max-w-7xl mx-auto space-y-8">
        <div>
            <h2 className="text-3xl font-bold text-slate-100 mb-2">Sleep Tracker</h2>
            <p className="text-slate-400 max-w-2xl">Log your sleep to discover patterns and improve your rest over time.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Log Sleep Form */}
            <div className="lg:col-span-1 bg-slate-800/50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Log Your Sleep</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="sleep-date" className="block text-sm font-semibold text-slate-300 mb-2">Date</label>
                        <input
                            id="sleep-date"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            max={today}
                            className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="sleep-duration" className="block text-sm font-semibold text-slate-300 mb-2">Duration (hours)</label>
                        <input
                            id="sleep-duration"
                            type="number"
                            value={duration}
                            onChange={(e) => setDuration(parseFloat(e.target.value))}
                            step="0.5"
                            min="0"
                            max="24"
                            className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-300 mb-2 text-center">Sleep Quality</label>
                        <QualityRating quality={quality} setQuality={setQuality} />
                    </div>
                    <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-4 rounded-lg transition-colors">
                        Save Log
                    </button>
                    {message && <p className="text-center text-sm text-green-400 mt-2">{message}</p>}
                </form>
            </div>

            {/* Data Display */}
            <div className="lg:col-span-2 space-y-8">
                {user?.sleepLog && user.sleepLog.length > 0 ? (
                    <>
                        <div className="grid grid-cols-2 gap-4 text-center">
                            <div className="bg-slate-800 p-4 rounded-lg">
                                <p className="text-sm text-slate-400">Avg. Duration</p>
                                <p className="text-2xl font-bold">{averageDuration} hrs</p>
                            </div>
                             <div className="bg-slate-800 p-4 rounded-lg">
                                <p className="text-sm text-slate-400">Avg. Quality</p>
                                <p className="text-2xl font-bold">{averageQuality} / 5</p>
                            </div>
                        </div>
                        <SleepChart logs={user.sleepLog} />
                    </>
                ) : (
                    <div className="bg-slate-800 p-10 rounded-lg text-center flex items-center justify-center h-full">
                        <p className="text-slate-400">You haven't logged any sleep yet. <br /> Add your first entry to see your sleep chart!</p>
                    </div>
                )}
            </div>
        </div>
    </div>
  );
};